/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Platform API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  AddApplicationNoteRequest,
  ApproveApplicationRequest,
  ApproveApplicationResponse,
  BrandApplication,
  BrandApplicationList,
  GetPlatformV1ApplicationsIdPathParameters,
  GetPlatformV1ApplicationsParams,
  PatchPlatformV1ApplicationsIdPathParameters,
  PostPlatformV1ApplicationsIdApprovePathParameters,
  PostPlatformV1ApplicationsIdNotesPathParameters,
  PostPlatformV1ApplicationsIdRejectPathParameters,
  RejectApplicationRequest,
  UpdateApplicationRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Without a status filter, applications still waiting for their email confirmation after 7 days are left out; status=pending_email shows them all
 * @summary List brand applications, newest first, with counts per status
 */
export const getPlatformV1Applications = (
    params?: GetPlatformV1ApplicationsParams,
 options?: SecondParameter<typeof customInstance<BrandApplicationList>>,) => {
      return customInstance<BrandApplicationList>(
      {url: `/platform/v1/applications`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Get a brand application
 */
export const getPlatformV1ApplicationsId = (
    { id }: GetPlatformV1ApplicationsIdPathParameters,
 options?: SecondParameter<typeof customInstance<BrandApplication>>,) => {
      return customInstance<BrandApplication>(
      {url: `/platform/v1/applications/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * contacted needs a confirmed application in new or contacted; withdrawn works on any open one. 409 CONFLICT with meta.status otherwise
 * @summary Mark an application contacted or withdrawn
 */
export const patchPlatformV1ApplicationsId = (
    { id }: PatchPlatformV1ApplicationsIdPathParameters,
    updateApplicationRequest: BodyType<UpdateApplicationRequest>,
 options?: SecondParameter<typeof customInstance<BrandApplication>>,) => {
      return customInstance<BrandApplication>(
      {url: `/platform/v1/applications/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateApplicationRequest
    },
      options);
    }
  /**
 * At most 50 notes per application (422 LIMIT_EXCEEDED)
 * @summary Add an internal note
 */
export const postPlatformV1ApplicationsIdNotes = (
    { id }: PostPlatformV1ApplicationsIdNotesPathParameters,
    addApplicationNoteRequest: BodyType<AddApplicationNoteRequest>,
 options?: SecondParameter<typeof customInstance<BrandApplication>>,) => {
      return customInstance<BrandApplication>(
      {url: `/platform/v1/applications/${id}/notes`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: addApplicationNoteRequest
    },
      options);
    }
  /**
 * Provisions the organization, its default store and the applicant as an invited owner in one transaction with the approval, then emails the owner a 7-day set-password link unless sendInvite is false. An applicant email that already has a user is attached as an active owner and gets no email. 409 ALREADY_EXISTS on body.slug when taken, 409 CONFLICT with meta.status unless the application is new or contacted
 * @summary Approve: provision the organization and invite the owner
 */
export const postPlatformV1ApplicationsIdApprove = (
    { id }: PostPlatformV1ApplicationsIdApprovePathParameters,
    approveApplicationRequest: BodyType<ApproveApplicationRequest>,
 options?: SecondParameter<typeof customInstance<ApproveApplicationResponse>>,) => {
      return customInstance<ApproveApplicationResponse>(
      {url: `/platform/v1/applications/${id}/approve`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: approveApplicationRequest
    },
      options);
    }
  /**
 * 409 CONFLICT with meta.status unless the application is new or contacted
 * @summary Reject an application, optionally emailing the reason
 */
export const postPlatformV1ApplicationsIdReject = (
    { id }: PostPlatformV1ApplicationsIdRejectPathParameters,
    rejectApplicationRequest: BodyType<RejectApplicationRequest>,
 options?: SecondParameter<typeof customInstance<BrandApplication>>,) => {
      return customInstance<BrandApplication>(
      {url: `/platform/v1/applications/${id}/reject`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: rejectApplicationRequest
    },
      options);
    }
  export type GetPlatformV1ApplicationsResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1Applications>>>
export type GetPlatformV1ApplicationsIdResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1ApplicationsId>>>
export type PatchPlatformV1ApplicationsIdResult = NonNullable<Awaited<ReturnType<typeof patchPlatformV1ApplicationsId>>>
export type PostPlatformV1ApplicationsIdNotesResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1ApplicationsIdNotes>>>
export type PostPlatformV1ApplicationsIdApproveResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1ApplicationsIdApprove>>>
export type PostPlatformV1ApplicationsIdRejectResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1ApplicationsIdReject>>>
